import z from 'zod';
import { ObjectIdSchema } from './objectId';
import { AssessmentSchema } from './report-research';
import { RiskLevel, RiskLevelSchema } from './risk';

export const ReportTypeSchema = z.enum(['KYB', 'KYC']);
export type ReportType = z.infer<typeof ReportTypeSchema>;
export const ReportType = ReportTypeSchema.enum;

export const ReportStatusSchema = z.enum(['DRAFT', 'PROCESSING', 'PARTIAL', 'FINISHED', 'FAILED']);
export type ReportStatus = z.infer<typeof ReportStatusSchema>;
export const ReportStatus = ReportStatusSchema.enum;

export const CompletedReportStatuses = [
  ReportStatus.FINISHED,
  ReportStatus.PARTIAL,
  ReportStatus.FAILED,
];
export const isReportCompleted = (status: string | null | undefined): boolean =>
  CompletedReportStatuses.some((completedStatus) => completedStatus === status);

export const getReportRiskLevel = (
  status: string | null | undefined,
  level?: string | null,
): RiskLevel => {
  if (!isReportCompleted(status)) {
    return RiskLevel.PENDING;
  }
  if (
    level === RiskLevel.HIGH ||
    level === RiskLevel.MEDIUM ||
    level === RiskLevel.LOW ||
    level === RiskLevel.UNASSESSED
  ) {
    return level;
  }
  return RiskLevel.UNASSESSED;
};

export const ReportListStatusSchema = z.enum([
  'ALL',
  'COMPLETED',
  'DRAFT',
  'PROCESSING',
  'FINISHED',
  'PENDING_REVIEW',
  'INCOMPLETE',
]);
export const ReportListStatus = ReportListStatusSchema.enum;
export const ReportSortOrderSchema = z.enum(['asc', 'desc']);
export const ReportSortOrder = ReportSortOrderSchema.enum;
export const ReportSortBySchema = z.enum(['createdAt', 'updatedAt']);
export const ReportSortBy = ReportSortBySchema.enum;

export const ReportAutofillStatusSchema = z.enum(['PENDING', 'PROCESSING', 'FAILED', 'FINISHED']);
export type ReportAutofillStatus = z.infer<typeof ReportAutofillStatusSchema>;
export const ReportAutofillStatus = ReportAutofillStatusSchema.enum;

export const ReportDecisionSchema = z.enum(['PENDING', 'ACCEPTED', 'REJECTED']);
export type ReportDecision = z.infer<typeof ReportDecisionSchema>;
export const ReportDecision = ReportDecisionSchema.enum;

export { AssessedRiskLevelSchema, RiskLevel, RiskLevelSchema } from './risk';

export const SignalTypeSchema = z.enum([
  'SANCTIONS',
  'ADVERSE_MEDIA',
  'PEP_SCREENING',
  'JURISDICTION_RISK',
  'SOCIAL',
]);
export type SignalType = z.infer<typeof SignalTypeSchema>;
export const SignalType = SignalTypeSchema.enum;

export const SignalResolutionSchema = z.enum(['PENDING', 'CONFIRMED', 'DISMISSED']);
export type SignalResolution = z.infer<typeof SignalResolutionSchema>;
export const SignalResolution = SignalResolutionSchema.enum;

export const SignalSchema = z.object({
  _id: ObjectIdSchema,
  reportId: ObjectIdSchema,
  subject: z.object({ kind: z.enum(['individual', 'company', 'member']), id: z.string() }),
  findingKey: z.string(),
  classification: z.enum(['INFORMATIONAL', 'DISCREPANCY', 'ADVERSE']).optional(),
  severity: z.enum(['NONE', 'MEDIUM', 'HIGH']).optional(),
  appliedRule: z.string().optional(),
  eventContext: z.string().optional(),
  activity: z
    .object({
      platform: z.string(),
      account: z.string(),
      author: z.string(),
      kind: z.string(),
      permalink: z.string(),
      publishedAt: z.string().nullable(),
    })
    .optional(),
  identityConfirmed: z.boolean().optional(),
  seriousFinancialCrime: z.boolean().default(false),
  match: z.enum(['matching', 'uncertain']),
  matchReason: z.string(),
  citations: z.array(z.object({ evidenceId: ObjectIdSchema, excerpt: z.string() })),
  rationale: z.string().optional(),
  type: SignalTypeSchema,
  title: z.string(),
  source: z.string(),
  sourceUrl: z.string().optional().nullable(),
  details: z.string().optional().nullable(),
  resolution: SignalResolutionSchema.optional(),
  resolvedByUserId: z.string().optional().nullable(),
  resolvedAt: z.date().optional().nullable(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});
export type TSignal = z.infer<typeof SignalSchema>;

export const DocumentTypeKYBSchema = z.enum([
  'CERTIFICATE_OF_INCORPORATION',
  'TRADE_LICENSE',
  'COMMERCIAL_REGISTRY_EXTRACT',
  'MERGER_AND_ACQUISITION',
]);
export type DocumentTypeKYB = z.infer<typeof DocumentTypeKYBSchema>;
export const DocumentTypeKYB = DocumentTypeKYBSchema.enum;

export const DocumentTypeKYCSchema = z.enum([
  'PASSPORT',
  'NATIONAL_ID',
  'RESIDENCY_PERMIT',
  'VOTER_ID',
]);
export type DocumentTypeKYC = z.infer<typeof DocumentTypeKYCSchema>;
export const DocumentTypeKYC = DocumentTypeKYCSchema.enum;

export const DocumentTypeSchema = z.union([
  DocumentTypeKYCSchema,
  DocumentTypeKYBSchema,
  z.literal('ADDITIONAL'),
]);
export type DocumentType = z.infer<typeof DocumentTypeSchema>;
export const DocumentType = {
  ...DocumentTypeKYC,
  ...DocumentTypeKYB,
  ADDITIONAL: 'ADDITIONAL',
} as const;

export const DocumentSideSchema = z.enum(['FRONT', 'BACK']);
export type DocumentSide = z.infer<typeof DocumentSideSchema>;
export const DocumentSide = DocumentSideSchema.enum;

export const DocumentExtractionStatusSchema = z.enum([
  'PENDING',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
]);
export type DocumentExtractionStatus = z.infer<typeof DocumentExtractionStatusSchema>;
export const DocumentExtractionStatus = DocumentExtractionStatusSchema.enum;

// ── Subject-level information schemas ──────────────────────────────────────

export const ClientInformationSchema = z.object({
  _id: ObjectIdSchema,
  firstName: z.string('First name is required'),
  nativeName: z.string().optional().nullable(),
  profileUrl: z.string().optional().nullable(),
  employer: z.string().optional().nullable(),
  lastName: z.string('Last name is required'),
  dateOfBirth: z.string().optional().nullable(),
  placeOfBirth: z.string().optional().nullable(),
  nationality: z.string().optional().nullable(),
  residenceCountry: z.string().optional().nullable(),
  taxIdentificationNumber: z.string().optional().nullable(),
  idNumber: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
  address: z.string().optional().nullable(),
  employmentStatus: z.string().optional().nullable(),
  sourceOfFunds: z.string().optional().nullable(),
  accountUse: z.string().optional().nullable(),
  netWorth: z.string().optional().nullable(),
});
export type ClientInformation = z.infer<typeof ClientInformationSchema>;

export const CompanyInformationSchema = z.object({
  nativeName: z.string().optional().nullable(),
  operatingCountry: z.string().optional().nullable(),
  companyName: z.string().min(1, 'Company name is required'),
  companyRegistrationNumber: z.string().optional().nullable(),
  companyIdNumber: z.string().optional().nullable(),
  companyTaxId: z.string().optional().nullable(),
  sector: z.string().optional().nullable(),
  industry: z.string().optional().nullable(),
  countryOfIncorporation: z.string().min(1, 'Country of incorporation is required'),
  incorporationDate: z.string().optional().nullable(),
  registeredAddress: z.string().optional().nullable(),
  annualRevenue: z.string().optional().nullable(),
  netWorth: z.string().optional().nullable(),
  ultimateBeneficialOwners: z.string().optional().nullable(),
  directors: z.string().optional().nullable(),
  contactEmail: z.string().optional().nullable(),
  contactPhone: z.string().optional().nullable(),
});
export type CompanyInformation = z.infer<typeof CompanyInformationSchema>;

export const KeyMemberSchema = z.object({
  nativeName: z.string().optional().nullable(),
  profileUrl: z.string().optional().nullable(),
  employer: z.string().optional().nullable(),
  _id: ObjectIdSchema,
  memberRole: z.string().min(1, 'Member role is required'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  dateOfBirth: z.string().optional().nullable(),
  placeOfBirth: z.string().optional().nullable(),
  nationality: z.string().optional().nullable(),
  countryOfResidence: z.string().optional().nullable(),
  taxIdentificationNumber: z.string().optional().nullable(),
  idNumber: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
  primarySourceOfFundsIncome: z.string().optional().nullable(),
  estimatedNetWorth: z.string().optional().nullable(),
  address: z.string().optional().nullable(),
  signals: z.array(SignalSchema).optional(),
});
export type KeyMember = z.infer<typeof KeyMemberSchema>;

// ── Extracted document data schemas ────────────────────────────────────────

export const ExtractedDocumentValidationIssuesSchema = z.object({
  documentWarnings: z
    .array(z.string())
    .describe('Issues: blurry text, cut-off edges, suspected tampering, or unreadable document.'),
  isExpired: z
    .boolean()
    .nullable()
    .describe('True if the expiry date has passed relative to today.'),
  expiryDate: z.string().nullable().describe('Document expiration date in YYYY-MM-DD format.'),
  isValidDocument: z
    .boolean()
    .describe('False if the document appears fake, purely handwritten, or severely damaged.'),
});
export type ExtractedDocumentValidationIssues = z.infer<
  typeof ExtractedDocumentValidationIssuesSchema
>;

export const ExtractedKeyMemberSchema = KeyMemberSchema.omit({
  _id: true,
  signals: true,
}).partial();
export type ExtractedKeyMember = z.infer<typeof ExtractedKeyMemberSchema>;

export const ExtractedKycDataSchema = ExtractedDocumentValidationIssuesSchema.extend({
  fieldProvenance: z
    .array(
      z.object({
        field: z.string(),
        value: z.string(),
        source: z.enum(['visual', 'mrz']),
        documentType: z.string(),
      }),
    )
    .optional(),
  clientInformation: ClientInformationSchema.omit({
    _id: true,
  })
    .partial()
    .describe('All personal information visible on the document.'),
  mrzFullString: z
    .string()
    .nullable()
    .optional()
    .describe('Raw Machine Readable Zone text from the bottom of the passport or ID card.'),
  mrzData: z
    .object({
      documentNumber: z.string().nullable().optional(),
      nationality: z.string().nullable().optional(),
      dateOfBirth: z.string().nullable().optional(),
      sex: z.string().nullable().optional(),
      expiryDate: z.string().nullable().optional(),
    })
    .optional()
    .describe('Parsed fields from the MRZ lines.'),
});
export type ExtractedKycData = z.infer<typeof ExtractedKycDataSchema>;

export const ExtractedKybDataSchema = ExtractedDocumentValidationIssuesSchema.extend({
  fieldProvenance: ExtractedKycDataSchema.shape.fieldProvenance,
  companyInformation: CompanyInformationSchema.partial().describe(
    'All company information visible on the document.',
  ),
  keyMembers: z
    .array(ExtractedKeyMemberSchema)
    .optional()
    .describe('Directors, UBOs, shareholders, or key signatories identified in the document.'),
});
export type ExtractedKybData = z.infer<typeof ExtractedKybDataSchema>;

export const ExtractedDataUnionSchema = z.union([ExtractedKycDataSchema, ExtractedKybDataSchema]);
export type ExtractedDataUnion = z.infer<typeof ExtractedDataUnionSchema>;
// ── Document & Report schemas ───────────────────────────────────────────────

export const CaseSchema = z.object({
  reportId: ObjectIdSchema,
  organizationId: z.cuid2(),
  createdByUser: z.cuid2(),
  reportType: ReportTypeSchema,
  reportName: z.string(),
  riskLevel: RiskLevelSchema,
  createdAt: z.date(),
});
export type Case = z.infer<typeof CaseSchema>;

export const DocumentVerificationSchema = z.object({
  status: z.enum(['VERIFIED', 'PROBLEMS_DETECTED', 'UNASSESSED']),
  checkedAt: z.coerce.date(),
  issues: z.array(
    z.object({
      field: z.string(),
      message: z.string(),
      evidence: z.string(),
      page: z.number().optional(),
    }),
  ),
  checks: z.array(z.string()),
  limitations: z.array(z.string()),
});
export const DocumentSchema = z.object({
  sourceReportId: z.string().optional(),
  _id: ObjectIdSchema,
  fileId: z.string(),
  fileName: z.string().optional().nullable(),
  storageKey: z.string().optional().nullable(),
  mimeType: z.string().optional().nullable(),
  documentType: DocumentTypeSchema,
  side: DocumentSideSchema.optional().nullable(),
  extractedData: ExtractedDataUnionSchema.nullable().optional(),
  extractionStatus: DocumentExtractionStatusSchema,
  isAcceptedByUser: z.boolean(),
  uploadedAt: z.coerce.date().optional(),
  verification: DocumentVerificationSchema.optional(),
});

export type Document = z.infer<typeof DocumentSchema>;

export const ReportSchema = z.object({
  clientId: ObjectIdSchema.optional(),
  generatedAt: z.coerce.date().optional(),
  reviewHistory: z.array(z.record(z.string(), z.unknown())).optional(),
  _id: ObjectIdSchema,
  reportName: z.string(),
  organizationId: z.cuid2(),
  createdByUserId: z.cuid2(),
  reportType: ReportTypeSchema,
  status: ReportStatusSchema,
  autofillStatus: ReportAutofillStatusSchema,
  decision: z
    .object({
      outcome: ReportDecisionSchema,
      decidedAt: z.date().optional(),
      decidedByUserId: z.cuid2().optional(),
      rationale: z.string().optional(),
      assessmentRevision: z.number().optional(),
      acknowledgedHighRisk: z.boolean().optional(),
    })
    .optional(),
  riskLevel: RiskLevelSchema.optional(),
  riskSummary: z.string().optional(),
  generationId: ObjectIdSchema.optional(),
  revision: z.number().default(0),
  assessment: AssessmentSchema.optional(),
  signals: z.array(SignalSchema).optional(),
  documents: z.array(DocumentSchema).optional(),
  clientInformation: ClientInformationSchema.nullable().optional(),
  companyInformation: CompanyInformationSchema.nullable().optional(),
  keyMembers: z.array(KeyMemberSchema).optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type Report = z.infer<typeof ReportSchema>;
